/**
 * Parser de montos mexicanos a partir de texto OCR de comprobantes bancarios.
 * Maneja miles (1,389.00), centavos (679.00), formato europeo (1.389,00)
 * y dígitos sin separador con centavos implícitos (138900 → 1389.00).
 */

const ETIQUETAS_MONTO = [
  'cantidad',
  'cantidad pagada',
  'cantidad transferida',
  'monto',
  'importe',
  'total',
  'transferencia exitosa',
  'monto transferido',
  'monto del depósito',
  'monto del deposito',
  'cantidad depositada',
  'abono',
  'depósito',
  'deposito',
]

const BANCOS_OCR = [
  { id: 'Azteca', patterns: ['BANCO AZTECA', 'GUARDADITO', 'AZTECA'] },
  { id: 'BBVA', patterns: ['BBVA', 'BBUA', 'BANCOMER'] },
  { id: 'Banorte', patterns: ['BANORTE', 'IXE'] },
  { id: 'HSBC', patterns: ['HSBC'] },
  { id: 'Santander', patterns: ['SANTANDER'] },
  { id: 'Banregio', patterns: ['BANREGIO', 'BANREDIO'] },
  { id: 'Scotia', patterns: ['SCOTIABANK', 'SCOTIA'] },
  { id: 'Inbursa', patterns: ['INBURSA'] },
  { id: 'Afirme', patterns: ['AFIRME'] },
  { id: 'Banamex', patterns: ['CITIBANAMEX', 'BANAMEX'] },
  { id: 'Spin', patterns: ['SPIN BY OXXO', 'SPIN'] },
]

function normalizarTextoMonto(raw) {
  if (raw == null || raw === '') return ''
  return String(raw)
    .trim()
    .replace(/[°]/g, '0')
    .replace(/\u00a0/g, ' ')
}

/** Cuenta/CLABE enmascarada (****0571) — no es monto. */
export function textoPareceCuentaEnmascarada(raw) {
  const t = normalizarTextoMonto(raw)
  if (!t) return false
  if (/[*•●∙·X]{2,}\s*\d{2,}/i.test(t)) return true
  if (/\b\d{2,4}\s*[-–]\s*(cuenta|clabe)\b/i.test(t)) return true
  return false
}

export function lineaPareceCuentaOClabe(linea) {
  const t = normalizarTextoMonto(linea)
  if (!t) return false
  if (textoPareceCuentaEnmascarada(t)) return true
  const upper = t.toUpperCase()
  if (/\bCLABE\b/.test(upper)) return true
  if (/\bCUENTA\s+(ORIGEN|DESTINO|DE\s+DESTINO|DE\s+ORIGEN)\b/.test(upper)) return true
  if (/\bCUENTA\s+VISION\b/.test(upper)) return true
  return false
}

/**
 * Dos puntos (1.269.00) o coma y punto (1,269.00 / 1.269,00):
 * el segundo separador es siempre el decimal.
 */
function parseMontoUltimoSeparadorDecimal(s) {
  if (!s) return null
  const nPuntos = (s.match(/\./g) || []).length
  const nComas = (s.match(/,/g) || []).length
  if (nPuntos < 2 && !(nPuntos >= 1 && nComas >= 1)) return null

  const lastDot = s.lastIndexOf('.')
  const lastComma = s.lastIndexOf(',')
  const lastSep = Math.max(lastDot, lastComma)
  if (lastSep <= 0) return null

  const enteros = s.slice(0, lastSep).replace(/[.,]/g, '')
  const decimales = s.slice(lastSep + 1).replace(/[.,]/g, '')
  if (!enteros || !/^\d+$/.test(enteros) || !/^\d{1,2}$/.test(decimales)) return null

  const n = parseFloat(`${enteros}.${decimales}`)
  return Number.isFinite(n) ? n : null
}

/**
 * @param {string} raw
 * @param {{ impliedDecimals?: boolean }} opts
 * @returns {number|null}
 */
export function parseMontoMexicanoOCR(raw, opts = {}) {
  let s = normalizarTextoMonto(raw)
  if (!s) return null

  if (textoPareceFechaCompacta(s)) return null
  if (/^\d{1,2}:\d{2}(:\d{2})?\s*$/.test(s)) return null
  if (textoPareceCuentaEnmascarada(s)) return null

  s = s.replace(/\b(MN|MXN|MXP|M\.N\.?|PESOS?)\b/gi, '').trim()

  const fragmento = s.match(/-?\d[\d.,\s]*/)
  if (!fragmento) return null
  s = fragmento[0].replace(/\s/g, '')

  if (!s || s === '-' || s === '.') return null

  // Inscripciones / comprobantes: 2 puntos (1.269.00) o coma+punto (1,269.00 / 1.269,00)
  // el segundo (último) separador siempre es el decimal.
  const porUltimoDecimal = parseMontoUltimoSeparadorDecimal(s)
  if (porUltimoDecimal != null) return porUltimoDecimal

  // Europeo: 1.389,00 | 12.345,67 | 679,00
  if (/^-?\d{1,3}(\.\d{3})+,\d{1,2}$/.test(s) || /^-?\d+,\d{1,2}$/.test(s)) {
    const n = parseFloat(s.replace(/\./g, '').replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }

  // MX/US: 1,389.00 | 12,345.67
  if (/^-?\d{1,3}(,\d{3})+\.\d{1,2}$/.test(s)) {
    const n = parseFloat(s.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  // Solo comas: miles (1,389) o decimal (679,00)
  if (s.includes(',') && !s.includes('.')) {
    const partes = s.split(',')
    const ultimo = partes[partes.length - 1]
    if (ultimo.length <= 2) {
      const n = parseFloat(s.replace(',', '.'))
      return Number.isFinite(n) ? n : null
    }
    if (ultimo.length === 3 && partes.length >= 2) {
      const n = parseFloat(s.replace(/,/g, ''))
      return Number.isFinite(n) ? n : null
    }
  }

  // Punto decimal sin comas: 1389.00 | 679.00 | OCR Banorte 1.389.00 | 1.26900
  if (s.includes('.') && !s.includes(',')) {
    const partes = s.split('.')
    const dec = partes[partes.length - 1]

    // 1.389.00 o 12.345.67 — punto como separador de miles + centavos
    if (partes.length >= 3 && dec.length === 2 && /^\d{2}$/.test(dec)) {
      const enteros = partes.slice(0, -1).join('')
      if (enteros.length >= 1) {
        const n = parseFloat(`${enteros}.${dec}`)
        if (Number.isFinite(n)) return n
      }
    }

    // $1,269.00 leído como 1.26900 (coma→punto y centavos pegados) → 1269.00
    // parseFloat('1.26900') = 1.269 ≈ 1.27
    if (partes.length === 2 && partes[0].length <= 2 && /^\d{5}$/.test(dec)) {
      const n = parseFloat(`${partes[0]}${dec.slice(0, 3)}.${dec.slice(3)}`)
      if (Number.isFinite(n) && n >= 100) return n
    }

    // OCR confunde miles: 1.389 (sin centavos) → 1389
    if (partes.length === 2 && dec.length === 3 && partes[0].length <= 2) {
      const comoMiles = parseFloat(partes[0] + dec)
      if (Number.isFinite(comoMiles) && comoMiles >= 100) return comoMiles
    }

    // 1389.00 | 679.00 (un solo punto decimal)
    if (partes.length === 2 && dec.length === 2 && /^\d{2}$/.test(dec)) {
      const n = parseFloat(s)
      return Number.isFinite(n) ? n : null
    }

    const n = parseFloat(s)
    return Number.isFinite(n) ? n : null
  }

  // Solo dígitos — centavos implícitos (apps BBVA/Banregio)
  const soloDigitos = s.replace(/[^0-9]/g, '')
  if (soloDigitos && !s.includes('.') && !s.includes(',')) {
    if (/^0{4,}/.test(soloDigitos)) return null
    if (/^0\d{2,3}$/.test(soloDigitos)) return null // 0571 de ****0571
    if (/^87\d{5}$/.test(soloDigitos)) return null
    if (opts.impliedDecimals && soloDigitos.length >= 3) {
      const enteros = soloDigitos.slice(0, -2)
      const centavos = soloDigitos.slice(-2)
      const n = parseFloat(`${enteros}.${centavos}`)
      return Number.isFinite(n) ? n : null
    }
    const n = parseFloat(soloDigitos)
    return Number.isFinite(n) ? n : null
  }

  const limpio = s.replace(/[^0-9.]/g, '')
  const n = parseFloat(limpio)
  return Number.isFinite(n) ? n : null
}

/**
 * @param {string} textoCompleto
 * @param {Array<{LineText: string}>} palabras
 * @returns {string}
 */
export function detectarBancoComprobante(textoCompleto, palabras = []) {
  const lineas = (palabras || [])
    .map((p) => (p && p.LineText ? p.LineText : ''))
    .join('\n')
  const blob = `${textoCompleto || ''}\n${lineas}`.toUpperCase()

  for (const banco of BANCOS_OCR) {
    if (banco.patterns.some((p) => blob.includes(p))) return banco.id
  }
  return ''
}

function lineaTieneEtiquetaMonto(linea) {
  const t = (linea || '').toLowerCase()
  return ETIQUETAS_MONTO.some((et) => t.includes(et))
}

const MESES_ABREV_OCR = 'ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC'
const REGEX_FECHA_COMPACTA = new RegExp(
  `^\\d{1,2}\\s*(${MESES_ABREV_OCR})\\s*\\d{2,4}$`,
  'i'
)

function textoPareceFechaCompacta(texto) {
  const t = normalizarTextoMonto(texto).replace(/\s/g, '')
  if (!t) return false
  return REGEX_FECHA_COMPACTA.test(t)
}

function lineaPareceHoraOFecha(linea) {
  const t = (linea || '').toLowerCase().trim()
  if (!t) return false
  if (/\b\d{1,2}:\d{2}(:\d{2})?\b/.test(t)) return true
  if (/^\d{1,2}:\d{2}(:\d{2})?\s*$/.test(t)) return true
  if (/\bhs\.?\b/.test(t)) return true
  if (/\ba las\b/.test(t)) return true
  if (/\b(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/.test(t)) return true
  if (new RegExp(`\\b(${MESES_ABREV_OCR})\\b`, 'i').test(t)) return true
  if (textoPareceFechaCompacta(t)) return true
  if (/^\d{1,2}\s*hs/i.test(t)) return true
  if (/^fecha\s*:?\s*\d/i.test(t)) return true
  if (/^hora\s*:?\s*\d/i.test(t)) return true
  return false
}

/** Líneas de ticket TPV (HSBC/Global Payments) que no son montos. */
export function lineaEsMetadataTicket(linea) {
  const t = normalizarTextoMonto(linea)
  if (!t) return false
  if (lineaPareceHoraOFecha(t)) return true
  if (lineaPareceCuentaOClabe(t)) return true
  if (lineaPareceDireccionOCalle(t)) return true

  const upper = t.toUpperCase()
  if (/^(FECHA|HORA)\b/.test(upper)) return true
  if (/\b(AUT|OPER|LOTE|REF|AID|ARQC|TC)\s*:/.test(upper)) return true
  if (/^(CREDITO|DEBITO|MASTERCARD|VISA|APROBADA|VENTA|GLOBALPAYMENTS)\b/.test(upper)) return true
  if (/^NUMERO DE TARJETA\b/i.test(t)) return true
  if (/^CAJA\s*:?\s*\d+/i.test(t)) return true
  if (/^COMISI[OÓ]N\b/i.test(t) && !/\$\s*[1-9]/.test(t)) return true

  const soloDigitos = upper.replace(/\D/g, '')
  if (/^0{4,}\d{1,8}$/.test(soloDigitos)) return true
  // IDs de terminal Global Payments / HSBC (8747361, OCR partido "874" / "8747")
  if (/^874\d*$/.test(soloDigitos)) return true
  if (textoPareceFechaCompacta(t)) return true

  return false
}

/** Dirección / número exterior (ej. GUERNICA NO 127) — no es monto. */
export function lineaPareceDireccionOCalle(linea) {
  const t = normalizarTextoMonto(linea)
  if (!t) return false
  const upper = t.toUpperCase()
  if (/\b(N[O°º]|NO\.?|NUM\.?|NUMERO|#)\s*\d{1,5}\b/.test(upper) && !/\$|IMPORTE|MONTO|MXN|MN\b/i.test(upper)) {
    return true
  }
  if (/\b(CALLE|AV\.?|AVENIDA|COL\.?|COLONIA|C\.?P\.?|CP)\b/.test(upper) && !/\$|IMPORTE|MONTO/i.test(upper)) {
    return true
  }
  return false
}

/** Contexto tipo "NO 127" / "N° 127" antes del número. */
function matchEsNumeroDeCalle(texto, matchIndex) {
  const ctx = texto.slice(Math.max(0, matchIndex - 12), matchIndex).toUpperCase()
  return /\b(N[O°º]|NO\.?|NUM\.?|NUMERO|#)\s*$/.test(ctx.trimEnd())
}

function parseMontoDesdeLineaImporte(linea) {
  if (!linea || lineaPareceCuentaOClabe(linea)) return null
  // No cortar por metadata en la misma línea si trae IMPORTE + $
  const t = normalizarTextoMonto(linea)
  const inlineConSigno = t.match(/IMPORTE[^\n$]{0,40}\$\s*([\d.,]+)/i)
  if (inlineConSigno) {
    const m = parseMontoMexicanoOCR(inlineConSigno[1])
    if (m != null && m >= 20) return m
  }
  const inlineDec = t.match(/IMPORTE\s*:?\s*([\d]+[.,]\d{2}|\d{1,3}(?:[.,]\d{3})+[.,]?\d{0,2})/i)
  if (inlineDec) {
    const m = parseMontoMexicanoOCR(inlineDec[1])
    if (m != null && m >= 20) return m
  }
  if (t.includes('$') || /\b(MN|MXN|MXP)\b/i.test(t)) {
    if (lineaEsMetadataTicket(t) && !/IMPORTE/i.test(t)) return null
    const m = parseMontoMexicanoOCR(t)
    if (m != null && m >= 20) return m
  }
  return null
}

/** Monto en línea con etiqueta Monto/Cantidad (SPEI Banorte, etc.). */
function parseMontoDesdeLineaEtiqueta(linea) {
  if (!linea || lineaPareceCuentaOClabe(linea) || lineaEsMetadataTicket(linea)) return null
  const t = normalizarTextoMonto(linea)
  const inline = t.match(
    /(?:MONTO|CANTIDAD(?:\s+(?:PAGADA|TRANSFERIDA|DEPOSITADA))?|IMPORTE|ABONO|DEP[OÓ]SITO)\s*:?\s*\$?\s*([\d.,\s]+)/i
  )
  if (inline) {
    const m = parseMontoMexicanoOCR(inline[1])
    if (m != null && m >= 1) return m
  }
  if (t.includes('$') || /\b(MN|MXN|MXP)\b/i.test(t)) {
    const m = parseMontoMexicanoOCR(t)
    if (m != null && m >= 1) return m
  }
  return null
}

/**
 * Monto de tickets de terminal (HSBC / Global Payments): prioriza IMPORTE + $.
 * @param {{ palabras?: Array<{LineText: string}>, textoCompleto?: string }} params
 * @returns {number|null}
 */
export function extraerMontoTicketTarjeta({ palabras = [], textoCompleto = '' }) {
  const lineas = (palabras || []).map((p) => (p && p.LineText ? p.LineText : ''))
  const texto = textoCompleto || lineas.join('\n')

  // Por si el overlay no trae bien las líneas pero ParsedText sí
  if (texto) {
    const m = texto.match(/IMPORTE[^\n$]{0,60}\$\s*([\d.,]+)/i)
    if (m) {
      const desdeTexto = parseMontoMexicanoOCR(m[1])
      if (desdeTexto != null && desdeTexto >= 20) return desdeTexto
    }
  }

  for (let i = 0; i < lineas.length; i++) {
    if (!/IMPORTE/i.test(lineas[i])) continue

    const mismo = parseMontoDesdeLineaImporte(lineas[i])
    if (mismo != null) return mismo

    for (const offset of [1, 2]) {
      const siguiente = lineas[i + offset]
      if (!siguiente) continue
      if (lineaEsMetadataTicket(siguiente) && !siguiente.includes('$')) continue
      if (lineaPareceDireccionOCalle(siguiente) || lineaPareceHoraOFecha(siguiente)) continue
      // Nunca tomar enteros sueltos (29 de 16:01:29) como importe
      const tieneSenalMonto = /\$|\b(MN|MXN|MXP)\b|[.,]\d{2}/i.test(siguiente)
      if (!tieneSenalMonto) continue
      const m = parseMontoMexicanoOCR(siguiente)
      if (m != null && m >= 20) return m
    }
  }

  const candidatos = []
  lineas.forEach((linea) => {
    if (!linea || lineaEsMetadataTicket(linea) || lineaPareceDireccionOCalle(linea)) return
    if (!linea.includes('$')) return
    const m = parseMontoMexicanoOCR(linea)
    if (m != null && m >= 20) candidatos.push(m)
  })

  if (candidatos.length) return Math.max(...candidatos)

  // Sin IMPORTE/$ no adivinar desde IDs de terminal (874 de 8747361)
  const pareceTicketTerminal = lineas.some(
    (l) =>
      /GLOBAL\s*PAYMENTS/i.test(l || '') ||
      /^874\d*$/.test(String(l || '').replace(/\D/g, '')) ||
      /^VENTA$/i.test(String(l || '').trim())
  ) || /GLOBAL\s*PAYMENTS/i.test(texto)
  if (pareceTicketTerminal) return null

  return extraerMontoPrincipalOCR({
    textoCompleto: texto,
    palabras,
    montoMinimo: 20,
    montoMaximo: 100000,
  })
}

function puntuarCandidato(monto, meta = {}) {
  let score = monto
  if (meta.porEtiqueta) score += 50000
  if (meta.porImporte) score += 80000
  if (meta.tieneMoneda) score += 10000
  if (meta.tieneSigno) score += 25000
  if (meta.tieneDecimales) score += 20000
  if (meta.bancoContexto) score += 3000
  if (monto <= 0) score -= 100000
  if (monto > 0 && monto < 20) score -= 8000
  if (meta.esHoraOFecha) score -= 100000
  if (meta.esMinutoHora && monto > 0 && monto < 60) score -= 50000
  if (meta.esCuentaOClabe) score -= 200000
  if (meta.esDireccion) score -= 200000
  // Entero corto sin $ ni decimales: típico de ****0571 → 571 o NO 127
  if (meta.esEnteroCortoSinMoneda) score -= 60000
  return score
}

function agregarCandidato(lista, monto, meta) {
  if (monto == null || !Number.isFinite(monto) || monto <= 0) return
  lista.push({ monto, score: puntuarCandidato(monto, meta), meta })
}

function extraerDeLinea(linea, opts, metaBase = {}) {
  const candidatos = []
  const lineaNorm = normalizarTextoMonto(linea)
  if (!lineaNorm) return candidatos

  if (lineaPareceCuentaOClabe(lineaNorm)) return candidatos
  if (lineaPareceDireccionOCalle(lineaNorm) && !metaBase.porEtiqueta && !metaBase.porImporte) {
    return candidatos
  }

  const esHoraOFecha = lineaPareceHoraOFecha(lineaNorm)
  const tieneSigno = lineaNorm.includes('$')
  const tieneMoneda = /\b(MN|MXN|MXP)\b/i.test(lineaNorm)
  const tieneDecimales = /[.,]\d{2}\b/.test(lineaNorm) || /[.,]\d{2}\s*$/.test(lineaNorm)
  const meta = {
    ...metaBase,
    tieneMoneda,
    tieneSigno,
    tieneDecimales,
    esHoraOFecha,
    esMinutoHora: esHoraOFecha && !tieneSigno && !tieneMoneda,
    esCuentaOClabe: false,
    esDireccion: lineaPareceDireccionOCalle(lineaNorm),
  }

  if (lineaEsMetadataTicket(lineaNorm) && !metaBase.porEtiqueta && !metaBase.porImporte) return candidatos
  if (esHoraOFecha && !meta.tieneSigno && !meta.tieneMoneda) return candidatos

  const directo = parseMontoMexicanoOCR(lineaNorm, opts)
  agregarCandidato(candidatos, directo, {
    ...meta,
    esEnteroCortoSinMoneda:
      !tieneSigno && !tieneMoneda && !tieneDecimales && directo != null && directo < 10000 && Number.isInteger(directo),
  })

  if (lineaNorm.includes('$')) {
    const partes = lineaNorm.split('$')
    const despuesSigno = partes[partes.length - 1]
    const m = parseMontoMexicanoOCR(despuesSigno, opts)
    agregarCandidato(candidatos, m, { ...meta, tieneSigno: true, tieneDecimales: tieneDecimales || /[.,]\d{2}/.test(despuesSigno) })
  }

  const matches = lineaNorm.match(/\d[\d.,\s]{0,14}\d|\d[\d.,]+/g) || []
  matches.forEach((frag) => {
    if (textoPareceCuentaEnmascarada(frag)) return
    const digitosFrag = String(frag).replace(/\D/g, '')
    if (/^874\d*$/.test(digitosFrag) && !tieneSigno) return
    const m = parseMontoMexicanoOCR(frag, opts)
    const fragDec = /[.,]\d{2}/.test(frag)
    agregarCandidato(candidatos, m, {
      ...meta,
      tieneDecimales: meta.tieneDecimales || fragDec,
      esEnteroCortoSinMoneda:
        !tieneSigno && !tieneMoneda && !fragDec && m != null && m < 10000 && Number.isInteger(m),
    })
  })

  return candidatos
}

/**
 * @param {object} params
 * @param {string} params.textoCompleto
 * @param {Array<{LineText: string}>} params.palabras
 * @param {string} [params.banco]
 * @param {boolean} [params.impliedDecimals]
 * @param {number} [params.montoMinimo]
 * @param {number} [params.montoMaximo]
 * @returns {number|null}
 */
export function extraerMontoPrincipalOCR({
  textoCompleto = '',
  palabras = [],
  banco = '',
  impliedDecimals = false,
  montoMinimo = 1,
  montoMaximo = 500000,
}) {
  const opts = { impliedDecimals: impliedDecimals || banco === 'BBVA' }
  const candidatos = []
  const lineas = (palabras || []).map((p) => (p && p.LineText ? p.LineText : ''))

  // $1 + 269.00 en líneas seguidas (apps Azteca/SPEI)
  for (let i = 0; i < lineas.length - 1; i++) {
    const a = String(lineas[i] || '')
    const b = String(lineas[i + 1] || '')
    if (!/\$/.test(a) || lineaPareceHoraOFecha(a) || lineaPareceHoraOFecha(b)) continue
    if (!/\d{3}/.test(b)) continue
    extraerDeLinea(`${a} ${b}`, opts, { tieneSigno: true, tieneDecimales: true }).forEach((c) =>
      candidatos.push(c)
    )
  }

  const textoMiles = textoCompleto || lineas.join('\n')
  const reMiles = /\$?\s*(\d{1,3})[.,\s](\d{3})[.,](\d{2})/g
  let matchMiles
  while ((matchMiles = reMiles.exec(textoMiles)) !== null) {
    const n = parseFloat(`${matchMiles[1]}${matchMiles[2]}.${matchMiles[3]}`)
    agregarCandidato(candidatos, n, {
      tieneSigno: /\$/.test(matchMiles[0]),
      tieneDecimales: true,
      bancoContexto: !!banco,
    })
  }

  for (let i = 0; i < lineas.length; i++) {
    const linea = lineas[i]
    if (
      lineaPareceHoraOFecha(linea) ||
      lineaEsMetadataTicket(linea) ||
      lineaPareceCuentaOClabe(linea) ||
      lineaPareceDireccionOCalle(linea)
    ) {
      continue
    }
    const porEtiqueta = lineaTieneEtiquetaMonto(linea)
    const porImporte = /IMPORTE/i.test(linea)
    const metaEtiqueta = {
      porEtiqueta,
      porImporte,
      bancoContexto: !!banco,
      tieneMoneda: false,
      tieneSigno: false,
    }

    if (porEtiqueta || porImporte) {
      const mismo = parseMontoDesdeLineaEtiqueta(linea) || parseMontoDesdeLineaImporte(linea)
      if (mismo != null) {
        agregarCandidato(candidatos, mismo, {
          ...metaEtiqueta,
          tieneSigno: linea.includes('$'),
          tieneDecimales: /[.,]\d{2}/.test(linea),
          tieneMoneda: /\b(MN|MXN|MXP)\b/i.test(linea),
        })
      }

      ;[lineas[i + 1], lineas[i + 2]].forEach((l) => {
        if (!l) return
        if (lineaPareceCuentaOClabe(l) || lineaPareceDireccionOCalle(l)) return
        if (lineaEsMetadataTicket(l) && !l.includes('$')) return
        // Tras etiqueta Monto, priorizar solo líneas con $ / moneda / decimales
        const tieneSenalMonto = /\$|\b(MN|MXN|MXP)\b|[.,]\d{2}/i.test(l)
        if (!tieneSenalMonto) return
        extraerDeLinea(l, opts, metaEtiqueta).forEach((c) => candidatos.push(c))
      })
    }

    if (/\$|MN|MXN|MXP/i.test(linea) && !lineaPareceCuentaOClabe(linea) && !lineaPareceDireccionOCalle(linea)) {
      extraerDeLinea(linea, opts, { bancoContexto: !!banco }).forEach((c) => candidatos.push(c))
    }
  }

  // 1) Montos con $: tomar el bloque completo ($ 1059.00), no solo 3 dígitos
  const regexConSigno = /\$\s*(-?[\d.,]+)/g
  let match
  const texto = textoCompleto || lineas.join('\n')
  while ((match = regexConSigno.exec(texto)) !== null) {
    const ctxAntes = texto.slice(Math.max(0, match.index - 8), match.index)
    if (/[*•●∙·X]{2,}\s*$/i.test(ctxAntes)) continue
    if (lineaPareceCuentaOClabe(texto.slice(Math.max(0, match.index - 40), match.index + match[0].length + 10))) {
      continue
    }
    const monto = parseMontoMexicanoOCR(match[1], opts)
    agregarCandidato(candidatos, monto, {
      tieneSigno: true,
      tieneDecimales: /[.,]\d{2}/.test(match[1]),
      tieneMoneda: /\b(MN|MXN)\b/i.test(texto.slice(match.index, match.index + 30)),
      bancoContexto: !!banco,
    })
  }

  // 2) Montos con decimales / miles sin $ (evitar enteros sueltos: 29, 127, 874)
  const regexDecimales = /\b(-?\d{1,3}(?:[.,]\d{3})+[.,]\d{2}|-?\d+[.,]\d{2})\b/g
  while ((match = regexDecimales.exec(texto)) !== null) {
    const ctxAntes = texto.slice(Math.max(0, match.index - 12), match.index)
    if (/[*•●∙·X]{2,}\s*$/i.test(ctxAntes)) continue
    if (/:\s*$/.test(ctxAntes)) continue // parte de hora 16:01:29
    if (matchEsNumeroDeCalle(texto, match.index)) continue
    if (lineaPareceCuentaOClabe(texto.slice(Math.max(0, match.index - 40), match.index + match[0].length + 10))) {
      continue
    }
    const monto = parseMontoMexicanoOCR(match[1], opts)
    agregarCandidato(candidatos, monto, {
      tieneSigno: false,
      tieneDecimales: true,
      tieneMoneda: /\b(MN|MXN)\b/i.test(texto.slice(match.index, match.index + 20)),
      bancoContexto: !!banco,
    })
  }

  const filtrados = candidatos.filter(
    (c) => c.monto >= montoMinimo && c.monto <= montoMaximo && !(c.meta && c.meta.esDireccion)
  )

  if (!filtrados.length) return null

  // Preferir monto con $ / decimales / etiqueta frente a enteros sueltos (NO 127, CAJA 1)
  const conSenal = filtrados.filter(
    (c) =>
      (c.meta && c.meta.tieneSigno) ||
      (c.meta && c.meta.tieneDecimales) ||
      (c.meta && c.meta.tieneMoneda) ||
      (c.meta && c.meta.porImporte) ||
      (c.meta && c.meta.porEtiqueta)
  )
  const pool = conSenal.length
    ? conSenal
    : filtrados.filter((c) => !(c.meta && c.meta.esEnteroCortoSinMoneda))
  const finalPool = pool.length ? pool : filtrados

  finalPool.sort((a, b) => b.score - a.score)
  return finalPool[0].monto
}

/**
 * Indica si el monto OCR parece truncado (ej. 1.39 en vez de 1389).
 * @param {number} actual
 * @param {number} candidato
 * @returns {boolean}
 */
export function montoOcrPareceIncorrecto(actual, candidato) {
  if (!actual || actual <= 0) return true
  if (!candidato || candidato <= 0) return false
  if (candidato > actual && candidato >= actual * 5) return true
  if (actual < 50 && candidato >= 100) return true
  return false
}
